/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";

const MicroSlider = ({images, duration = 3000, position}) => {
    const [index, setIndex] = useState(0);
    const isTransitioning = useRef(false);
    const startX = useRef(0);
    const isDragging = useRef(false);

    useEffect(() => {
        const interval = setInterval(
            () => {
                if (index + 1 === images.length) {
                    //return to first image
                    isTransitioning.current = true;
                    setIndex(0);
                    setTimeout(() => (isTransitioning.current = false), 0);
                } else {
                    //transition loop
                    setIndex(i => i + 1);
                }
            },
            //offset the start by 1 second using position index
            duration + (index === 0 ? position * 1000 : 0)
        );
        return () => clearInterval(interval);
    }, [index, images.length, duration]);

    const handleSwipeNext = () => {
        if (index + 1 === images.length) {
            //return to first image
            setIndex(0);
        } else {
            //transition loop
            setIndex(index + 1);
        }
    };
    // Handle touch/mouse swiping
    const handleSwipeStart = e => {
        startX.current = e.touches ? e.touches[0].clientX : e.clientX;
        isDragging.current = true;
    };
    const handleSwipeMove = e => {
        if (!isDragging.current) return;
        const currentX = e.touches ? e.touches[0].clientX : e.clientX;
        const deltaX = currentX - startX.current;
        if (deltaX > 50) {
            setIndex(i => (i === 0 ? images.length - 1 : i - 1));
            isDragging.current = false;
        } else if (deltaX < -50) {
            setIndex(i => (i === images.length - 1 ? 0 : i + 1));
            isDragging.current = false;
        }
    };
    const handleSwipeEnd = () => {
        isDragging.current = false;
    };
    return (
        <div
            className="MicroSlider w-full relative overflow-hidden select-none rounded-2xl aspect-[64/56] cursor-pointer"
            onMouseDown={handleSwipeStart}
            onMouseLeave={handleSwipeEnd}
            onMouseMove={handleSwipeMove}
            onMouseUp={handleSwipeEnd}
            onTouchEnd={handleSwipeEnd}
            onTouchMove={handleSwipeMove}
            onTouchStart={handleSwipeStart}
            onClick={handleSwipeNext}
        >
            <div
                className={`flex ${isTransitioning.current ? "" : "transition-transform duration-500"} transform`}
                style={{transform: `translateX(-${index * 100}%)`}}
                onTransitionEnd={() => index === images.length && setIndex(0)}
            >
                {images.map((img, i) => (
                    <img key={i} alt="" className="w-full flex-shrink-0 h-full object-cover object-center select-none pointer-events-none" src={img} />
                ))}
            </div>

            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                {images.map((_, i) => (
                    <div
                        key={i}
                        className={`w-5 h-0.5 cursor-pointer ${
                            index === i || (index === images.length && i === 0) ? "bg-white opacity-100" : "bg-white opacity-50"
                        }`}
                        onClick={() => setIndex(i)}
                    />
                ))}
            </div>
        </div>
    );
};

export default MicroSlider;
